import { PogNonCircular } from "@CommonTypes/merchflow/pog/pog";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Button, Color, Flex, Icon, Scroller, Spinner, Text } from "src/elements";
import { useTranslation } from "src/i18n";
import { AgentComposer } from "./components/AgentComposer";
import { AgentIntentPicker } from "./components/AgentIntentPicker";
import { AgentMessageCard, describeError, followUpLabel } from "./components/AgentMessageCard";
import { FollowUpAction, canApplyHandover, followUpFor, previewPogFor } from "./store/followUp";
import { ChatMessage, usePogAgentChat } from "./store/hooks";
import { PogAgentRunResponse } from "./store/types";
import type { PogAgentFailure } from "./store/api";
import { ReviewButton } from "../../pages/SpacePlanning/review/reviewDesign";
import { ReviewColor } from "../../pages/SpacePlanning/review/reviewDesignTokens";
import { translate } from "src/i18n/translate";

const friendlyFailure = (error: PogAgentFailure) => {
  if (error.kind === "unreachable") return translate("pogAgent", "embedded.failure.unreachable");
  if (error.kind === "agent" && error.status === 409)
    return translate("pogAgent", "embedded.failure.planChanged");
  if (error.kind === "agent" && (error.status === 401 || error.status === 403))
    return translate("pogAgent", "embedded.failure.session");
  return translate("pogAgent", "embedded.failure.generic");
};
const InlineThread = styled.div`
  flex: 1 1 160px;
  min-height: 160px;
  min-width: 0;
  overflow: auto;
  padding: 2px 2px 8px;
  scrollbar-width: thin;
  scrollbar-color: ${ReviewColor.line} transparent;
`;

const ChatSurface = styled(Flex)<{ $embedded: boolean }>`
  ${({ $embedded }) =>
    $embedded &&
    `
    color: ${ReviewColor.ink};
    font-size: 13px;
    line-height: 20px;
    span { font-size: 13px; line-height: 20px; text-transform: none; letter-spacing: normal; }
    summary { color: ${ReviewColor.muted}; cursor: pointer; font-size: 12px; }
  `}
`;

const EmptyThread = styled.section`
  padding: 16px 4px;
  color: ${ReviewColor.ink};
  h3 {
    margin: 0 0 8px;
    font-size: 15px;
    line-height: 22px;
    font-weight: 600;
  }
  p {
    margin: 0 0 10px;
    color: ${ReviewColor.muted};
    font-size: 13px;
    line-height: 20px;
  }
  p:last-child {
    margin-bottom: 0;
  }
`;

interface Props {
  previewOnly?: boolean;
  embedded?: boolean;
  context?: string;
  suggestedPrompt?: { id: string; text: string };
  restrictToPog?: boolean;
  onBusyChange?: (busy: boolean) => void;
  sourceHash?: string | null;
  assessPreview?: (pog: PogNonCircular) => { summary: string; blocked: string | null };
  merchflowId: number;
  storeCode: string;
  storePogId: number;
  /**
   * Whether the panel is on screen. The page keeps it mounted while hidden so
   * a run that finishes after the panel was closed still refreshes the canvas
   * and the thread is still there when it is reopened.
   */
  isOpen?: boolean;
  /** Stop new turns — the canvas holds unsaved edits the agent cannot see. */
  isBlocked?: boolean;
  /** True while the canvas is showing a preview this panel put there. */
  isShowingPreview: boolean;
  /**
   * The save revision at which the preview on the canvas was saved as-is, so
   * this store's POG already holds the plan and only the rest of the merchflow
   * is left to do. Null when the last save was something else. A number rather
   * than a flag so two previews saved in a row are each attributed.
   */
  savedPreviewRevision?: number | null;
  /**
   * Bumped by the page on every successful save of this POG. A preview made
   * against an older revision is stale: the agent would now work from a
   * different saved layout, so it is offered a re-run instead of an apply.
   */
  savedPogRevision?: number;
  /**
   * Asked before anything the agent does would redraw the canvas. False means
   * the merchandiser chose to keep what is on screen, so the action is dropped.
   */
  confirmCanvasOverwrite?: () => Promise<boolean>;
  /** Draw a previewed layout on the canvas as an unsaved change. May throw. */
  onShowPreview: (pog: PogNonCircular) => void;
  onDiscardPreview?: () => void;
  /** Reload the planogram from OWA once the agent has written to it. */
  onPogWritten: () => void;
  /**
   * True while a write is out to the agent. The page locks the canvas and
   * Save for that long, so no hand edit can land between the apply starting
   * and the refetch that replaces what is drawn.
   */
  onApplyingChange?: (isApplying: boolean) => void;
  /**
   * "Apply as new version" made a new store POG and left this one untouched.
   * The page should move to it, as it does after "Save New Version".
   */
  onPogCreated?: (storePogId: number) => void;
  onClose: () => void;
}

export const PogAgentChat = ({
  previewOnly = false,
  embedded = false,
  context,
  suggestedPrompt,
  restrictToPog = false,
  onBusyChange,
  sourceHash,
  assessPreview,
  merchflowId,
  storeCode,
  storePogId,
  isOpen = true,
  isBlocked = false,
  isShowingPreview,
  savedPreviewRevision = null,
  savedPogRevision = 0,
  confirmCanvasOverwrite,
  onShowPreview,
  onDiscardPreview,
  onPogWritten,
  onApplyingChange,
  onPogCreated,
  onClose,
}: Props) => {
  const { t } = useTranslation("pogAgent");
  const {
    messages,
    isRunning,
    runKind,
    progress,
    applicableMessageId,
    sendPrompt,
    applyStoredPlan,
    reportError,
  } = usePogAgentChat({
    previewOnly,
    context,
    restrictToPog,
    sourceHash,
    merchflowId,
    target: { pogId: storePogId, storeCode },
    savedPogRevision,
    onPogWritten,
    onPogCreated,
  });

  const refThread = useRef<HTMLDivElement>(null);
  const refLatestAssistant = useRef<HTMLDivElement>(null);
  const refProgress = useRef<HTMLDivElement>(null);
  const lastAssistantId = messages.findLast((message) => message.role === "assistant")?.id;
  const initialAssistantId = useRef(lastAssistantId);
  const lastMessageId = messages.at(-1)?.id;

  const isApplying = runKind === "apply";
  useEffect(() => {
    onBusyChange?.(isRunning);
  }, [isRunning, onBusyChange]);
  useEffect(() => {
    onApplyingChange?.(isApplying);
  }, [isApplying]);
  /** Which turn's preview is on the canvas, so only that card says so. */
  const [previewMessageId, setPreviewMessageId] = useState<string | null>(null);
  /** The same id in a ref, so a save can be attributed without re-running below. */
  const refPreviewMessageId = useRef<string | null>(null);
  /** The turn whose plan the merchandiser saved onto this store themselves. */
  const [savedPlanMessageId, setSavedPlanMessageId] = useState<string | null>(null);

  /**
   * A preview outlived by a save of this POG. Each turn carries the revision
   * it was requested under, so a save that lands while the agent is still
   * working counts too. The one exception is the save of the preview itself,
   * which puts the plan on the store rather than under it.
   */
  const isStalePreview = (message: ChatMessage): boolean => {
    if (message.role !== "assistant" || message.response.mode !== "preview") return false;
    if (!canApplyHandover(message.response) || message.id === savedPlanMessageId) return false;

    return message.pogRevision !== savedPogRevision;
  };

  /** The prompt that produced a turn: what the merchandiser typed just before it. */
  const promptFor = (messageId: string): string | null => {
    const index = messages.findIndex((message) => message.id === messageId);
    const userMessage = messages.slice(0, index).findLast((message) => message.role === "user");

    return userMessage ? userMessage.text : null;
  };

  /** Both agent actions below replace what is drawn, so both ask first. */
  const mayOverwriteCanvas = () =>
    confirmCanvasOverwrite ? confirmCanvasOverwrite() : Promise.resolve(true);

  const showOnCanvas = async (messageId: string, response: PogAgentRunResponse) => {
    const pog = previewPogFor(response);
    if (!pog) return;
    if (!(await mayOverwriteCanvas())) return;

    try {
      onShowPreview(pog);
      refPreviewMessageId.current = messageId;
      setPreviewMessageId(messageId);
    } catch (error) {
      reportError({
        kind: "unknown",
        detail: t("errors.drawFailed", {
          detail: error instanceof Error ? error.message : String(error),
        }),
      });
    }
  };

  const applyPlan = async (action: FollowUpAction) => {
    if (previewOnly) return;
    const applicable = messages.find((message) => message.id === applicableMessageId);
    if (applicable?.role === "assistant") {
      const pog = previewPogFor(applicable.response);
      const blocked = pog && assessPreview?.(pog).blocked;
      if (blocked) {
        reportError({ kind: "unknown", detail: blocked });
        return;
      }
    }
    if (!(await mayOverwriteCanvas())) return;

    await applyStoredPlan(action, followUpLabel(action, t));
  };
  useEffect(() => {
    if (!embedded || !lastAssistantId || lastAssistantId === initialAssistantId.current) return;
    const message = messages.find((entry) => entry.id === lastAssistantId);
    if (
      message?.role === "assistant" &&
      message.id === applicableMessageId &&
      !isStalePreview(message) &&
      message.response.mode === "preview" &&
      previewPogFor(message.response)
    ) {
      void showOnCanvas(message.id, message.response);
    }
  }, [embedded, lastAssistantId, applicableMessageId]);

  // Attribute the save to whichever turn was drawn at that moment. Reading the
  // ref keeps a later preview from inheriting the credit. Keyed on the save
  // revision, so saving preview A and then preview B attributes each in turn.
  useEffect(() => {
    setSavedPlanMessageId(savedPreviewRevision !== null ? refPreviewMessageId.current : null);
  }, [savedPreviewRevision]);

  useEffect(() => {
    if (embedded) {
      if (!isOpen) return;
      const target = isRunning ? refProgress.current : refLatestAssistant.current;
      const scroller = refThread.current;
      if (target && scroller) {
        scroller.scrollTop +=
          target.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
      }
      return;
    }
    const thread = refThread.current;
    if (thread) {
      thread.scrollTop = thread.scrollHeight;
    }
  }, [messages, isRunning, isOpen, embedded]);

  if (!isOpen) return null;
  const Thread = embedded ? InlineThread : Scroller;

  return (
    <ChatSurface
      $embedded={embedded}
      column
      gap={embedded ? "6px" : "10px"}
      width={embedded ? "100%" : "380px"}
      minWidth={embedded ? "0" : "380px"}
      minHeight={embedded ? "254px" : "0"}
      padding={embedded ? "0" : "10px"}
      style={embedded ? { flex: "1 1 0", overflow: "hidden" } : undefined}
      background={embedded ? ReviewColor.surface : Color.lightGray}
      borderColor={embedded ? undefined : Color.spaceGray}
      borderRadius={embedded ? "0" : "4px"}
    >
      {!embedded && (
        <Flex justify="between" align="center" gap="10px">
          <Flex gap="6px" align="center">
            <Icon name="ai" color={Color.primary} size={16} />

            <Text variant="h5">{embedded ? t("embedded.title") : t("title")}</Text>
          </Flex>

          <Button
            variant="borderless"
            size="small"
            ariaLabel={t("close")}
            iconLeft={{ name: "close" }}
            onClick={onClose}
          />
        </Flex>
      )}

      {!embedded && (
        <Text variant="small2" color={Color.textSecondary}>
          {t("target", { merchflowId, storeCode, storePogId })}
        </Text>
      )}
      {context && !embedded && (
        <Text variant="small2" color={Color.textSecondary}>
          {embedded ? t("embedded.context") : context}
        </Text>
      )}

      <Thread ref={refThread} data-testid={embedded ? "pog-agent-response-thread" : undefined}>
        <Flex column gap="8px" padding="2px">
          {embedded && messages.length === 0 && !isRunning && (
            <EmptyThread>
              <h3>{t("embedded.emptyTitle")}</h3>
              <p>{t("embedded.emptyBody")}</p>
              <p>{t("embedded.emptyExample")}</p>
            </EmptyThread>
          )}
          {messages.length === 0 && !embedded && (
            <Text variant="small2" color={Color.textSecondary}>
              {embedded
                ? previewOnly
                  ? t("embedded.introPreviewOnly")
                  : t("embedded.intro")
                : t("intro")}
            </Text>
          )}

          {messages.map((message) => {
            if (message.role === "user") {
              return (
                <Flex key={message.id} justify="right">
                  <Flex
                    padding="8px 10px"
                    maxWidth="85%"
                    background={embedded ? ReviewColor.accentSoft : Color.greenSmoke}
                    borderRadius={embedded ? "12px 12px 3px 12px" : "4px"}
                  >
                    <Text variant="body3" wordBreak="break-word">
                      {message.text}
                    </Text>
                  </Flex>
                </Flex>
              );
            }

            if (message.role === "clarify") {
              return (
                <div
                  key={message.id}
                  ref={message.id === lastMessageId ? refLatestAssistant : undefined}
                >
                  <AgentIntentPicker
                    embedded={embedded}
                    key={message.id}
                    prompt={message.prompt}
                    // Only the newest turn is still open; a later prompt settles it.
                    isDisabled={isRunning || isBlocked || message.id !== lastMessageId}
                    onPick={(intent) => void sendPrompt(message.prompt, intent)}
                  />
                </div>
              );
            }

            if (message.role === "error") {
              return (
                <Flex
                  ref={message.id === lastMessageId ? refLatestAssistant : undefined}
                  key={message.id}
                  gap="6px"
                  align="center"
                  padding="8px 10px"
                  background={embedded ? ReviewColor.dangerBg : Color.alertHighBg}
                  borderColor={embedded ? ReviewColor.line : Color.alertHighBorder}
                  borderRadius={embedded ? "10px" : "4px"}
                >
                  <Icon name="alert" color={Color.red} size={14} />

                  <Text variant="small2" color={Color.alertHighText} wordBreak="break-word">
                    {embedded ? friendlyFailure(message.error) : describeError(message.error, t)}
                  </Text>
                  {embedded && (
                    <details>
                      <summary>{t("reviewCopy.details")}</summary>
                      <Text variant="small2" wordBreak="break-word">
                        {describeError(message.error, t)}
                      </Text>
                    </details>
                  )}
                </Flex>
              );
            }

            // The one card whose plan is still the stored one. A newer prompt
            // takes that away even before it has answered.
            const isApplicable = message.id === applicableMessageId;
            const previewForAssessment = previewPogFor(message.response);
            const isStale = isStalePreview(message);
            const prompt =
              (isApplicable || message.id === lastAssistantId) && isStale
                ? promptFor(message.id)
                : null;

            return (
              <div
                key={message.id}
                ref={message.id === lastAssistantId ? refLatestAssistant : undefined}
                data-testid={
                  message.id === lastAssistantId ? "pog-agent-latest-response" : undefined
                }
              >
                <AgentMessageCard
                  embedded={embedded}
                  restrictToPog={restrictToPog}
                  previewAssessment={
                    previewForAssessment && assessPreview
                      ? assessPreview(previewForAssessment)
                      : undefined
                  }
                  key={message.id}
                  response={message.response}
                  failure={message.failure}
                  followUp={
                    !previewOnly &&
                    isApplicable &&
                    !isStale &&
                    !(restrictToPog && message.response.mode === "apply")
                      ? followUpFor(message.response, message.id === savedPlanMessageId)
                      : null
                  }
                  isDisabled={isRunning || isBlocked}
                  isOnCanvas={isShowingPreview && message.id === previewMessageId}
                  isStale={isStale}
                  onFollowUp={(action) => void applyPlan(action)}
                  onShowOnCanvas={(response) => void showOnCanvas(message.id, response)}
                  onRerun={prompt ? () => void sendPrompt(prompt) : undefined}
                />
              </div>
            );
          })}

          {isRunning && (
            <Flex ref={refProgress} gap="6px" align="center">
              <Spinner />

              <Text variant="small2" color={Color.textSecondary}>
                {progress
                  ? t("applying", { done: progress.done, total: progress.total })
                  : t("working")}
              </Text>
            </Flex>
          )}
        </Flex>
      </Thread>

      {isShowingPreview && !embedded && (
        <Flex
          gap="6px"
          align="center"
          padding="8px 10px"
          background={Color.greenSmoke}
          borderColor={Color.primary}
          borderRadius="4px"
        >
          <Icon name="info" color={Color.primary} size={14} />

          <Text variant="small2" color={Color.textMain}>
            {t("banner.previewOnCanvas")}
          </Text>
        </Flex>
      )}

      {isBlocked && (
        <Flex
          gap="6px"
          align="center"
          padding="8px 10px"
          background={Color.warningBg}
          borderColor={Color.yellow}
          borderRadius="4px"
        >
          <Icon name="warningCircle" color={Color.warningIcon} size={14} />

          <Text variant="small2" color={Color.warningText}>
            {t("banner.blocked")}
          </Text>
        </Flex>
      )}

      <AgentComposer
        embedded={embedded}
        secondaryAction={
          embedded && isShowingPreview && onDiscardPreview ? (
            <ReviewButton
              type="button"
              size="small"
              variant="borderOnly"
              onClick={onDiscardPreview}
            >
              {t("reviewCopy.returnToSaved")}
            </ReviewButton>
          ) : undefined
        }
        suggestedPrompt={suggestedPrompt}
        isRunning={isRunning}
        isDisabled={isBlocked}
        onSend={(prompt) => void sendPrompt(prompt)}
      />
    </ChatSurface>
  );
};
